import React, { useState, useEffect, useCallback } from 'react'

import { useEditor } from '@toasttab/sites-components'

import {
  Modal,
  ModalContext,
  ModalOverlay
} from 'src/shared/components/common/modal/Modal'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useRestaurantRoutes } from 'src/shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { LocationSelectionGateContent } from './LocationSelectionGateContent'

export const GATE_DISMISSED_KEY = 'toast-location-gate-dismissed'
export const LOCATION_SELECTED_PARAM = 'locationSelected'

const getGateDismissed = () => {
  try {
    return sessionStorage.getItem(GATE_DISMISSED_KEY) === 'true'
  } catch {
    return false
  }
}

const setGateDismissed = () => {
  try {
    sessionStorage.setItem(GATE_DISMISSED_KEY, 'true')
  } catch {
    // sessionStorage unavailable
  }
}

/**
 * Gates OO-related pages behind explicit location selection for multi-location sites.
 *
 * Two-step flow:
 * 1. "Ordering from this location?" — shows the current (defaulted) location with
 *    Yes/No buttons. Yes dismisses the gate; No advances to the location picker.
 * 2. Location picker — radio list of all locations, guest must select and confirm.
 *
 * On mobile, renders as a bottom sheet. On desktop, renders as a centered modal.
 *
 * Controlled by the `requireLocationSelection` site config
 * Falls back to `oo-require-location-selection` feature flag until migration.
 *
 * Client-only: skipped during SSR.
 */
const LocationSelectionGate = ({ children }: React.PropsWithChildren<{}>) => {
  const {
    locations,
    selectedLocation,
    updateLocation,
    locationExplicitlySelected,
    restaurant
  } = useRestaurant()
  const { ooRequireLocationSelection } = useFlags()
  const { confirmationPath } = useRestaurantRoutes()
  const [isMounted, setIsMounted] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const { track } = useThrottledTracker()

  useEffect(() => {
    setIsMounted(true)
    if (getGateDismissed()) {
      setDismissed(true)
    } else {
      const params = new URLSearchParams(window.location.search)
      if (params.get(LOCATION_SELECTED_PARAM) === 'true') {
        setDismissed(true)
        setGateDismissed()
        // Clean up the URL param after consuming it
        params.delete(LOCATION_SELECTED_PARAM)
        const newSearch = params.toString()
        const newUrl =
          window.location.pathname + (newSearch ? `?${newSearch}` : '')
        window.history.replaceState({}, '', newUrl)
      }
    }
  }, [])

  const locationSelectionRequired =
    restaurant?.config?.requireLocationSelection ?? ooRequireLocationSelection

  const hasMultipleLocations = (locations?.length || 0) > 1
  const isConfirmationPage =
    isMounted && window.location.pathname.includes(confirmationPath)
  const shouldGate =
    isMounted &&
    locationSelectionRequired &&
    hasMultipleLocations &&
    !locationExplicitlySelected &&
    !dismissed &&
    !isConfirmationPage

  useEffect(() => {
    if (shouldGate) {
      track(TrackingEvents.LOCATION_SELECTION_GATE_SHOWN, {
        defaultRestaurantGuid: selectedLocation?.externalId,
        defaultLocationName: selectedLocation?.name,
        totalLocations: locations?.length,
        currentPage: window.location.pathname
      })
    }
  }, [shouldGate]) // eslint-disable-line react-hooks/exhaustive-deps

  const onConfirmCurrent = useCallback(() => {
    if (!selectedLocation) return
    track(TrackingEvents.LOCATION_SELECTION_GATE_CONFIRMED, {
      selectedRestaurantGuid: selectedLocation.externalId,
      selectedLocationName: selectedLocation.name,
      totalLocations: locations?.length,
      currentPage: window.location.pathname,
      changedLocation: false
    })
    setDismissed(true)
    setGateDismissed()
  }, [selectedLocation, locations, track])

  const onConfirmChange = useCallback(
    (locationGuid: string) => {
      const selectedLoc = locations?.find((l) => l.externalId === locationGuid)
      track(TrackingEvents.LOCATION_SELECTION_GATE_CONFIRMED, {
        selectedRestaurantGuid: locationGuid,
        selectedLocationName: selectedLoc?.name,
        totalLocations: locations?.length,
        currentPage: window.location.pathname,
        changedLocation: true
      })
      updateLocation(locationGuid)
      setDismissed(true)
      setGateDismissed()
    },
    [updateLocation, locations, track]
  )

  const onClose = useCallback(() => {
    setDismissed(true)
    setGateDismissed()
  }, [])

  return (
    <>
      {children}
      <Modal
        isOpen={shouldGate}
        onClose={onClose}
        preventOverlayClose
        testId='location-selection-gate'
      >
        <ModalOverlay />
        <LocationSelectionGateContent
          onConfirmCurrentLocation={onConfirmCurrent}
          onConfirmLocationChange={onConfirmChange}
          requestUserLocationOnChangeStep
        />
      </Modal>
    </>
  )
}

/**
 * Preview of the location selection gate for editor experience.
 *
 * Intentionally has none of the gate's behavior: no FF check,
 * sessionStorage dismissal, tracking, geolocation request, and confirming
 * does not change the location being edited.
 *
 */
const LocationSelectionPreview = () => {
  const { locationGatePreview, onLocationGatePreviewDismiss } = useEditor()
  const { selectedLocation } = useRestaurant()

  const dismiss = useCallback(() => {
    onLocationGatePreviewDismiss?.()
  }, [onLocationGatePreviewDismiss])

  if (!locationGatePreview || !selectedLocation) {
    return null
  }

  return (
    <ModalContext.Provider value={{ onClose: dismiss }}>
      <div
        className='modal editorLocationGate'
        data-testid='location-selection-gate-preview'
      >
        <ModalOverlay testId='location-selection-gate-preview-overlay' />
        <LocationSelectionGateContent
          onConfirmCurrentLocation={dismiss}
          onConfirmLocationChange={dismiss}
          onClose={dismiss}
          disableFocusTrap
        />
      </div>
    </ModalContext.Provider>
  )
}

const WrappedLocationSelectionGate = ({
  children
}: React.PropsWithChildren<{}>) => {
  const { isEditor } = useEditor()

  return isEditor ? (
    <LocationSelectionPreview />
  ) : (
    <LocationSelectionGate>{children}</LocationSelectionGate>
  )
}

export default WrappedLocationSelectionGate
